/* eslint-disable max-len */
import React, { useState } from 'react';
import {
  makeStyles, Box, Typography, Divider, Button, Grid, useMediaQuery, useTheme,
} from '@material-ui/core';
import Youtube from 'react-lazyload-youtube';

import Breadcrumbs from 'common/components/breadcrumbs/Breadcrumbs';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import Spec from 'common/components/Spec';
import IconHeight from 'common/icons/IconHeight';
import IconWeight from 'common/icons/IconWeight';
import IconMechanicGear from 'common/icons/IconMechanicGear';
import IconWidth from 'common/icons/IconWidth';
import IconFuel from 'common/icons/IconFuel';
import IconOdometer from 'common/icons/IconOdometer';
import ExpandableDescription from 'common/components/ExpandableDescription';
import { withTranslation } from 'config/i18n';
import dynamic from 'next/dynamic';
import extractNumberFromString from 'utils/extractNumberFromString';
import Modal from 'components/shared/Modal';
import 'react-lazyload-youtube/dist/index.css';
import StarIcon from '@material-ui/icons/Star';
import { useLayoutState } from 'components/shared/Layout';

const Share = dynamic(() => import('components/shared/Share'), {
  ssr: false,
});

const useStyles = makeStyles((theme) => ({
  titleHolder: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  title: {
    fontWeight: '500',
    color: theme.palette.secondary.dark,
  },
  subTitle: {
    color: theme.palette.secondary.dark,
    marginTop: theme.spacing(0.75),
    marginRight: theme.spacing(1),
  },
  sectionTitleHolder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontWeight: '500',
    marginRight: theme.spacing(2),
  },
  badges: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  badge: {
    backgroundColor: theme.palette.grey['50'],
    border: `1px solid ${theme.palette.grey['100']}`,
    padding: theme.spacing(0.375, 1.125),
    borderRadius: theme.typography.pxToRem(11),
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
    marginTop: theme.spacing(0.5),

    '& svg': {
      height: theme.typography.pxToRem(16),
      width: theme.typography.pxToRem(16),
      marginLeft: theme.spacing(0.5),
      color: theme.palette.warning.main,
    },

    '&:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
}));

const VehicleDescription = (props) => {
  const classes = useStyles();
  const {
    t, mainTitle, vehicle, locationHeader, breadCrumbsProps, vehicleDetails, specialDetails,
    header,
  } = props;
  const theme = useTheme();
  const { layoutState } = useLayoutState();

  const isMdScreen = useMediaQuery(theme.breakpoints.down('md'));
  // data comes with units (e.g 4.2 inch) from the api so we need to transform it to plain number
  const formattedSpecialDetails = {};
  Object.keys(specialDetails).forEach((key) => {
    formattedSpecialDetails[key] = extractNumberFromString(specialDetails[key]);
  });

  const [open, setOpen] = useState(false);
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Box>
        {open && (
          <Modal
            open={open}
            onClose={handleClose}
            data={header}
            content={<Youtube height="480px" videoId={vehicle.orientation_video_url} />}
          />
        )}
        <Box className={classes.titleHolder}>
          <Typography variant="h2" component="h1" className={classes.title}>
            {mainTitle}
          </Typography>

          {!layoutState.isCrawler && <Share text />}
        </Box>

        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" className={classes.subTitle}>
            {locationHeader}
          </Typography>

          <Box className={classes.badges}>
            {!!vehicle.rate && (
            <span className={classes.badge}>
              <span>{vehicle.rate}</span>
              <StarIcon />
            </span>
            )}

            {!!vehicle.trips_count && (
            <span className={classes.badge}>
              {vehicle.trips_count}
              {' '}
              {t('fe_es_user_info_sub_info_component:trips')}
            </span>
            )}
          </Box>
        </Box>

        <Breadcrumbs data={breadCrumbsProps} />

        <Box mb={2}>
          <Divider />
        </Box>

        <Box className={classes.sectionTitleHolder}>
          <Typography variant="h3" className={classes.sectionTitle}>
            {t('fe_es_vehicle_page:description')}
          </Typography>

          {vehicle?.orientation_video_url && (
            <Button
              color="primary"
              className={classes.button}
              onClick={handleClickOpen}
              endIcon={<PlayArrowIcon />}
            >
              {t('fe_es_vehicle_page:watch_model_vedio')}
            </Button>
          )}
        </Box>
        {/* I think  map will be better than manually typing every spec */}
        <Grid container spacing={isMdScreen ? 1 : null}>
          {formattedSpecialDetails.seat_height && (
            <Grid item xs={6} md={4} lg={2}>
              <Spec
                icon={<IconHeight />}
                title={t('fe_es_vehicle_page:seat_height')}
                amount={formattedSpecialDetails.seat_height}
              />
            </Grid>
          )}
          {formattedSpecialDetails.weight && (
            <Grid item xs={6} md={4} lg={2}>
              <Spec
                icon={<IconWeight />}
                title={t('fe_es_vehicle_page:weight')}
                amount={formattedSpecialDetails.weight}
              />
            </Grid>
          )}
          {formattedSpecialDetails.displacement && (
            <Grid item xs={6} md={4} lg={2}>
              <Spec
                icon={<IconMechanicGear />}
                title={t('fe_es_vehicle_page:cc')}
                amount={formattedSpecialDetails.displacement}
              />
            </Grid>
          )}
          {formattedSpecialDetails.length && (
            <Grid item xs={6} md={4} lg={2}>
              <Spec
                icon={<IconWidth />}
                title={t('fe_es_vehicle_page:length')}
                amount={formattedSpecialDetails.length}
              />
            </Grid>
          )}
          {formattedSpecialDetails.fuel_capacity && (
            <Grid item xs={6} md={4} lg={2}>
              <Spec
                icon={<IconFuel />}
                title={t('fe_es_vehicle_page:fuel')}
                amount={formattedSpecialDetails.fuel_capacity}
              />
            </Grid>
          )}
          {formattedSpecialDetails.fuel_economy && (
            <Grid item xs={6} md={4} lg={2}>
              <Spec
                icon={<IconOdometer />} // temporary solution
                title={t('fe_es_vehicle_page:fuel_economy')}
                amount={formattedSpecialDetails.fuel_economy}
              />
            </Grid>
          )}
        </Grid>

        <Box my={2}>
          <Divider light />
        </Box>

        <ExpandableDescription description={vehicleDetails.description} />
      </Box>
    </>
  );
};

export default withTranslation('fe_es_vehicle_page')(VehicleDescription);
